import { SendersTypes } from "../models/message.model";

const WEBVIEWER_URL_MARKER = "/ca-viewer/#/view";

/**
 * Regex: Matches web-viewer URLs that are NOT already part of an <a> tag
 * or attribute value (e.g. href="...").
 */
const WEBVIEWER_URL_REGEX: RegExp =
	/(?<!<\/a>)(https?:\/\/[^\s<>"']+[a-z0-9]{24})(?![^<>]*<\/a>)(?![^<>]*href=)(?![^<>]*['"])/g;

/**
 * Wraps plain-text web-viewer URLs in clickable <a> tags.
 * Only processes messages that appear to contain a web-viewer URL.
 */
export function linkifyWebviewerUrls(message: string): string {
	if (!message.includes(WEBVIEWER_URL_MARKER)) {
		return message;
	}
	return message.replace(WEBVIEWER_URL_REGEX, `<a href="$1" target="_blank">$1</a>`);
}

/**
 * Replaces <p>/<\/p> tags from the ngx-editor with <div>/<\/div>.
 */
export function sanitizeEditorHtml(message: string): string {
	return message.replaceAll("<p>", "<div>").replaceAll("</p>", "</div>");
}

/**
 * Returns a short display label for a sender type.
 */
export function getSendersTypeLabel(type: SendersTypes): string {
	switch (type) {
		case SendersTypes.Customer:
			return "DOC";
		case SendersTypes.System:
			return "SYS";
		case SendersTypes.Technician:
			return "TEC";
		case SendersTypes.Support:
			return "SUP";
		default:
			return "???";
	}
}

/**
 * Converts a data: URL to a Blob for opening in a new browser tab.
 */
export function dataUrlToBlob(dataUrl: string): Blob {
	const arr = dataUrl.split(",");
	const mime = arr[0].match(/:(.*?);/)[1];
	const bstr = atob(arr[1]);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);
	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}
	return new Blob([u8arr.buffer as ArrayBuffer], { type: mime });
}

/**
 * Opens an image in a new browser tab, handling both data: URLs and regular URLs.
 */
export function openImageInNewTab(imgSrc: string): void {
	if (imgSrc.startsWith("data:")) {
		const blob = dataUrlToBlob(imgSrc);
		window.open(URL.createObjectURL(blob), "_blank");
	} else {
		window.open(imgSrc, "_blank");
	}
}

/**
 * Regex matching only "empty" contents within a paragraph.
 *   \s           — any whitespace character (space, tab, newline)
 *   &nbsp;       — HTML non-breaking space entity
 *   &#0*160;     — decimal HTML entity for non-breaking space, with optional leading zeroes
 *   &#x0*[aA]0;  — hexadecimal HTML entity for non-breaking space (lower- or uppercase A), with optional leading zeroes
 *   <br\s*\/?>   — <br>, <br/> or <br /> linebreak tags with optional spaces/self-closing
 */
const EMPTY_PARAGRAPH_INNER = /(?:\s|&nbsp;|&#0*160;|&#x0*[aA]0;|<br\s*\/?>)*/;

/**
 * Trimmed HTML that is only empty paragraphs (ngx-editor-style).
 */
const EMPTY_EDITOR_PARAGRAPHS_ONLY_REGEX = new RegExp(
	`^(?:<p>${EMPTY_PARAGRAPH_INNER.source}<\\/p>\\s*)+$`,
	"i" // i: case-insensitive matching
);

/**
 * Lone `<br>` tags with optional whitespace (no paragraph wrapper).
 */
const EMPTY_EDITOR_BR_ONLY_REGEX = /^\s*(?:<br\s*\/?>\s*)+$/i;

/**
 * Checks if a message (raw ngx-editor HTML output string) is empty.
 * Treats whitespace, empty `<p>`, `<br>`, and common nbsp-only markup as empty.
 */
export function isMessageEmpty(message: string): boolean {
	const trimmed = message.trim();
	return (
		!trimmed ||
		EMPTY_EDITOR_PARAGRAPHS_ONLY_REGEX.test(trimmed) ||
		EMPTY_EDITOR_BR_ONLY_REGEX.test(trimmed)
	);
}
